import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Truck, ClipboardList, BarChart3, MapPin } from "lucide-react";
import MinePicker from "@/components/MinePicker";
import TripGrid from "@/components/TripGrid";
import ShiftManager from "@/components/ShiftManager";
import Reports from "@/components/Reports";
import { fetchActiveShift } from "@/api";

const TABS = [
  { key: "tripman", label: "TRIP MAN", icon: Truck, testid: "role-tab-tripman", roles: ["manager", "tripman"] },
  { key: "manager", label: "SHIFT MANAGER", icon: ClipboardList, testid: "role-tab-manager", roles: ["manager"] },
  { key: "reports", label: "REPORTS", icon: BarChart3, testid: "role-tab-reports", roles: ["manager", "tripman"] },
];

const storedMine = () => JSON.parse(localStorage.getItem("dumpops_mine") || "null");

function App() {
  const [mine, setMine] = useState(storedMine);
  const [tab, setTab] = useState("tripman");
  const [activeShift, setActiveShift] = useState(null);

  const refreshShift = useCallback(async () => {
    try {
      setActiveShift(await fetchActiveShift());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!mine) return;
    refreshShift();
    const t = setInterval(refreshShift, 10000);
    return () => clearInterval(t);
  }, [mine, refreshShift]);

  if (!mine) {
    return (
      <>
        <Toaster position="bottom-center" richColors />
        <MinePicker
          onSelect={(m) => {
            localStorage.setItem("dumpops_mine", JSON.stringify(m));
            setMine(m);
          }}
        />
      </>
    );
  }

  const visibleTabs = TABS;
  const currentTab = visibleTabs.some((t) => t.key === tab) ? tab : "tripman";

  return (
    <div className="App min-h-screen bg-white text-[#0A0A0A]">
      <Toaster position="bottom-center" richColors />
      <header className="border-b-2 border-black bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-0">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight uppercase" data-testid="app-title">
                Dump<span className="bg-[#FFB800] px-2 border-2 border-black">Ops</span> Hub
              </h1>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mt-1">
                Shovel-Dumper Trip Counting
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="change-mine-btn"
                onClick={() => {
                  localStorage.removeItem("dumpops_mine");
                  setActiveShift(null);
                  setMine(null);
                }}
                className="border-2 border-black bg-black text-white px-3 py-1.5 hard-shadow-sm pressable font-head font-bold text-sm uppercase flex items-center gap-1"
              >
                <MapPin size={14} strokeWidth={3} /> {mine.area_name} / {mine.mine_name}
              </button>
              <div data-testid="active-shift-banner">
                {activeShift ? (
                  <div className="border-2 border-black bg-[#FFB800] px-3 py-1.5 hard-shadow-sm font-head font-bold text-sm uppercase">
                    LIVE: {activeShift.name}
                  </div>
                ) : (
                  <div className="border-2 border-black bg-zinc-100 px-3 py-1.5 font-head font-bold text-sm uppercase text-zinc-500">
                    NO ACTIVE SHIFT
                  </div>
                )}
              </div>
            </div>
          </div>
          <nav className="flex gap-0 mt-4 -mb-[2px]" data-testid="role-switcher">
            {visibleTabs.map(({ key, label, icon: Icon, testid }) => (
              <button
                key={key}
                data-testid={testid}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-4 sm:px-6 min-h-[56px] font-head font-bold text-sm sm:text-base uppercase border-2 border-black pressable ${
                  currentTab === key ? "bg-[#FFB800] hard-shadow-sm" : "bg-white hover:bg-zinc-100 border-b-2"
                }`}
              >
                <Icon size={20} strokeWidth={2.5} />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{label.split(" ")[0]}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {currentTab === "tripman" && <TripGrid activeShift={activeShift} />}
        {currentTab === "manager" && <ShiftManager activeShift={activeShift} refreshShift={refreshShift} />}
        {currentTab === "reports" && <Reports mine={mine} />}
      </main>
    </div>
  );
}

export default App;
